/* LANTURN Studio: progressive enhancement; no external dependencies or tracking. */
(() => {
  if (!customElements.get('lt-menu')) {
    customElements.define('lt-menu', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = {signal:this.controller.signal};
        const details = this.querySelector('details');
        const summary = details?.querySelector('summary');
        if (!details || !summary) return;
        const close = (focus = false) => {
          if (!details.open) return;
          details.open = false;
          if (focus) summary.focus();
        };
        summary.setAttribute('aria-expanded', String(details.open));
        details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)), options);
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && details.open) {event.preventDefault();close(true);}
        }, options);
        this.addEventListener('click', event => {if(event.target.closest('a')) close();}, options);
        document.addEventListener('click', event => {if(!this.contains(event.target)) close();}, options);
        document.addEventListener('focusin', event => {if(!this.contains(event.target)) close();}, options);
      }
      disconnectedCallback() {this.controller?.abort();}
    });
  }
  if (!customElements.get('lt-cable-picker')) {
    customElements.define('lt-cable-picker', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const events = {signal:this.controller.signal};
        const powers = [...this.querySelectorAll('[data-picker-power]')];
        const lengths = [...this.querySelectorAll('[data-picker-length]')];
        const products = [...this.querySelectorAll('[data-picker-product]')];
        const empty = this.querySelector('[data-picker-empty]');
        const prompt = this.querySelector('[data-picker-prompt]');
        const live = this.querySelector('.lt-picker-live');
        let words;
        try {words = JSON.parse(this.querySelector('[data-picker-translations]').textContent);}
        catch {return;}
        const update = () => {
          powers.forEach(input => {input.disabled = !products.some(p => p.dataset.power === input.value);});
          const power = powers.find(input => input.checked && !input.disabled)?.value;
          lengths.forEach(input => {
            input.disabled = !power || !products.some(p => p.dataset.power === power && p.dataset.length === input.value);
            if (input.disabled) input.checked = false;
          });
          const length = lengths.find(input => input.checked && !input.disabled)?.value;
          const matches = products.filter(p => p.dataset.power === power && p.dataset.length === length);
          const selected = matches.find(p => p.dataset.available === 'true') || matches[0];
          products.forEach(product => {
            product.hidden = product !== selected;
            const submit = product.querySelector('button[type="submit"]');
            if (submit) submit.disabled = product !== selected || product.dataset.available !== 'true';
          });
          empty.hidden = Boolean(selected);
          prompt.textContent = !products.length ? words.no_match : !power ? words.choose_power : !length ? words.choose_length : words.no_match;
          live.textContent = selected
            ? ['[data-picker-selection]','[data-picker-price]','[data-picker-stock]'].map(selector => selected.querySelector(selector).textContent.trim()).join('. ')
            : prompt.textContent;
        };
        this.resetSelection = () => {
          [...powers,...lengths].forEach(input => {input.checked = false;});
          update();
        };
        this.addEventListener('change', event => {
          if (event.target.matches('[data-picker-power], [data-picker-length]')) update();
        }, events);
        this.addEventListener('submit', event => {
          const product = event.target.closest('[data-picker-product]');
          if (!product || product.hidden || product.dataset.available !== 'true') event.preventDefault();
        }, events);
        update();
      }
      disconnectedCallback() {this.controller?.abort();}
    });
  }
  if (!customElements.get('lt-finder')) {
    customElements.define('lt-finder', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const events = {signal:this.controller.signal};
        const form = this.querySelector('.lt-finder-form');
        const ui = this.querySelector('.lt-finder-ui');
        const fallback = this.querySelector('.lt-finder-fallback');
        const output = this.querySelector('.lt-finder-output');
        const manual = this.querySelector('[data-manual-picker]');
        const progress = this.querySelector('.lt-finder-progress');
        if (!form || !ui || !output || !manual) return;
        let words;
        try { words = JSON.parse(this.querySelector('[data-finder-translations]').textContent); }
        catch { return; }
        if (!words || !words.step) return;
        const t = (key, vars = {}) => String(words[key] || '').replace(/__([a-z_]+)__/g, (_,name) => vars[name] ?? '');
        const field = name => form.elements.namedItem(name);
        const status = output.querySelector('.lt-finder-status');
        const explanation = output.querySelector('.lt-finder-explanation');
        const help = output.querySelector('.lt-finder-help');
        const helpText = help.querySelector('[data-help-text]');
        const source = output.querySelector('.lt-finder-source');
        const sourceLinks = source.querySelector('[data-source-links]');
        const chargingDetails = output.querySelector('[data-charging-details]');
        const chargingNote = chargingDetails.querySelector('[data-charging-note]');
        const alternativeLink = chargingDetails.querySelector('[data-alternative-cable]');
        const matches = [...output.querySelectorAll('.lt-finder-match')];
        const nav = form.querySelector('.lt-finder-nav');
        const next = form.querySelector('[data-action="next"]');
        const database = window.LanturnVehicleData;
        const vehicles = database?.schemaVersion === 2 && Array.isArray(database.vehicles) ? database.vehicles.filter(v => v.market === 'EU' && typeof v.period === 'string' && v.sourceIds?.length) : [];
        const cableRatingFor = vehicle => {
          if (!vehicle || vehicle.adviceStatus !== 'approved' || vehicle.connector !== 'type2' || ![16,32].includes(vehicle.cableAmps) || !Number.isFinite(vehicle.acKw) || vehicle.acKw <= 0 || vehicle.acKw > 22 || ![1,2,3].includes(vehicle.acPhases)) return null;
          const perPhaseKw = vehicle.acKw / vehicle.acPhases;
          if (perPhaseKw > 7.4 || perPhaseKw > vehicle.cableAmps * 0.230 + 0.05) return null;
          if (vehicle.acPhases === 1) return vehicle.cableAmps === 16 ? 3.7 : 7.4;
          return vehicle.cableAmps === 16 ? 11 : 22;
        };
        let mode = null;
        let stage = 'mode';
        let previousStage = 'mode';
        const resetOutput = () => {
          output.hidden = true;
          matches.forEach(match => {
            match.hidden = true;
            const submit = match.querySelector('button[type="submit"]');
            if (submit) submit.disabled = true;
          });
          help.hidden = true;
          helpText.textContent = t('help_text');
          explanation.hidden = true;
          explanation.textContent = '';
          source.hidden = true;
          source.open = false;
          sourceLinks.replaceChildren();
          chargingDetails.hidden = true;
          chargingDetails.open = false;
          chargingNote.textContent = '';
          alternativeLink.hidden = true;
          alternativeLink.removeAttribute('href');
        };
        const focus = element => {
          element.setAttribute('tabindex','-1');
          element.focus({preventScroll:true});
          element.scrollIntoView?.({block:'nearest',behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        };
        const setStage = (value, moveFocus = true) => {
          stage = value;
          resetOutput();
          form.hidden = value === 'result' || value === 'manual';
          manual.hidden = value !== 'manual';
          progress.hidden = value === 'manual';
          form.querySelectorAll('[data-stage]').forEach(group => {group.hidden = group.dataset.stage !== value;group.disabled = group.hidden;});
          nav.hidden = value === 'mode' || value === 'result';
          const step = value === 'mode' ? 1 : ['vehicle','manual'].includes(value) ? 2 : 3;
          this.querySelector('[data-progress-text]').textContent = t('step',{current:step});
          this.querySelector('[data-progress-bar]').style.width = `${step / 3 * 100}%`;
          next.textContent = t(value === 'finish' ? 'show_result' : 'next');
          if (value === 'finish') {
            const vehicle = selectedVehicle();
            const rating = cableRatingFor(vehicle);
            const format = value => new Intl.NumberFormat(this.dataset.locale || 'nl').format(value);
            this.querySelector('[data-vehicle-preview]').textContent = vehicle && rating
              ? t('selection_preview',{car:`${vehicle.make} ${vehicle.model}`,power:format(vehicle.acKw),cable_power:format(rating)})
              : '';
          }
          if (moveFocus && value !== 'result') focus(value === 'manual' ? manual.querySelector('.lt-picker-title') : form.querySelector(`[data-stage="${value}"] legend`));
        };
        const populate = (name, list, labelKey) => {
          const select = field(name);
          select.replaceChildren(new Option(t(labelKey),''),...list.map(([value,label])=>new Option(label,value)));
          select.disabled = list.length === 0;
        };
        const unique = values => [...new Set(values)].sort((a,b)=>String(a).localeCompare(String(b),'nl',{numeric:true}));
        const updateMake = () => {
          populate('model',unique(vehicles.filter(v=>v.make === field('make').value).map(v=>v.model)).map(x=>[x,x]),'select_model');
          populate('year',[],'select_year');populate('vehicle',[],'select_trim');
        };
        const updateModel = () => {
          const rows = vehicles.filter(v=>v.make === field('make').value && v.model === field('model').value);
          populate('year',unique(rows.map(v=>v.period)).map(period=>[period,period]),'select_year');
          populate('vehicle',[],'select_trim');
        };
        const updateYear = () => {
          const rows = vehicles.filter(v=>v.make === field('make').value && v.model === field('model').value && v.period === field('year').value);
          populate('vehicle',rows.map(v=>[v.id,[v.trim,v.charger].filter(Boolean).join(' · ')]),'select_trim');
        };
        const selectedVehicle = () => vehicles.find(v=>v.id === field('vehicle').value && v.make === field('make').value && v.model === field('model').value && v.period === field('year').value);
        const showVehicleHelp = vehicle => {
          if (vehicle?.connector === 'type1') showHelp('not_type2','not_type2_help');
          else if (vehicle?.connector === 'none') showHelp('not_plugin','not_plugin_help');
          else showHelp('incomplete_specs','incomplete_specs_help');
        };
        const showHelp = (heading,detail) => {
          previousStage = stage === 'result' ? previousStage : stage;
          setStage('result');
          status.textContent = t(heading);
          if (detail) helpText.textContent = t(detail);
          help.hidden = false;
          output.hidden = false;
          focus(output);
        };
        const chooseMode = selected => {
          mode = selected;
          if (selected === 'vehicle' && !vehicles.length) { showHelp('database_error');return; }
          setStage(selected);
        };
        const showResult = () => {
          const v = selectedVehicle();
          if (!v) {showHelp('unknown_car');return;}
          const rating = cableRatingFor(v);
          if (!rating) {showVehicleHelp(v);return;}
          const candidates = matches.filter(m=>Number(m.dataset.power) === rating && Number(m.dataset.length) === Number(field('length').value));
          const match = candidates.find(m=>m.dataset.available === 'true') || candidates[0];
          if (!match) {showHelp('no_match');return;}
          previousStage = 'finish';
          setStage('result');
          const car = v ? `${v.make} ${v.model}` : '';
          status.textContent = match.dataset.available === 'true' ? (v ? t('vehicle_match',{car}) : t('manual_match')) : t('unavailable');
          if (v) {
            const power = new Intl.NumberFormat(this.dataset.locale || 'nl').format(v.acKw);
            const ratingText = v.acPhases === 2 ? 'rating_two_phase' : rating === 22 ? 'rating_22' : rating === 11 ? 'rating_11' : 'rating_small';
            explanation.textContent = [t('car_summary',{car,year:field('year').value,trim:[v.trim,v.charger].filter(Boolean).join(' · ')}),t('vehicle_specs',{power}),t(ratingText),v.customerNote || ''].filter(Boolean).join(' ');
            explanation.hidden = false;
            chargingDetails.hidden = false;
            chargingNote.textContent = [t('speed_hint'),rating === 11 ? t('charging_11') : ''].filter(Boolean).join(' ');
            if (rating === 11 && v.acPhases === 3) {
              const alternative = matches.find(m => Number(m.dataset.power) === 22 && m.dataset.length === match.dataset.length && m.dataset.available === 'true');
              const href = alternative?.querySelector('a[href]')?.getAttribute('href');
              if (href) {alternativeLink.href = href;alternativeLink.hidden = false;}
            }
            for (const id of v.sourceIds) {
              const entry = database.sources?.[id];
              if (!entry || !entry.url.startsWith('https://')) continue;
              const a = document.createElement('a');
              a.href = entry.url;a.textContent = entry.name;a.target = '_blank';a.rel = 'noopener noreferrer';
              sourceLinks.append(a);
            }
            const review = document.createElement('p');review.textContent = t('updated',{date:v.reviewed});sourceLinks.append(review);
            if (v.marketScope) {const market = document.createElement('p');market.textContent = t('source_market',{market:v.marketScope});sourceLinks.append(market);}
            source.hidden = false;
          }
          match.hidden = false;
          const submit = match.querySelector('button[type="submit"]');
          if (submit) submit.disabled = match.dataset.available !== 'true';
          output.hidden = false;
          focus(output);
        };
        const restart = () => {
          form.reset();mode = null;
          populate('make',unique(vehicles.map(v=>v.make)).map(x=>[x,x]),'select_make');
          populate('model',[],'select_model');populate('year',[],'select_year');populate('vehicle',[],'select_trim');
          field('length').disabled = false;
          manual.querySelector('lt-cable-picker')?.resetSelection?.();
          setStage('mode');
        };
        this.addEventListener('click',event => {
          const button = event.target.closest('button');
          if (!button || !this.contains(button)) return;
          if (button.dataset.mode) chooseMode(button.dataset.mode);
          if (button.dataset.action === 'back') setStage(stage === 'finish' ? mode : 'mode');
          if (button.dataset.action === 'restart') restart();
          if (button.dataset.action === 'edit') setStage(previousStage === 'result' ? 'mode' : previousStage);
          if (button.dataset.action === 'missing') showHelp('unknown_car');
        },events);
        form.addEventListener('change',event => {
          if (event.target.name === 'make') updateMake();
          if (event.target.name === 'model') updateModel();
          if (event.target.name === 'year') updateYear();
          if (['make','model','year','vehicle'].includes(event.target.name)) field('length').value = '';
        },events);
        form.addEventListener('submit',event => {
          event.preventDefault();
          if (!form.reportValidity()) return;
          if (stage === 'vehicle' && !selectedVehicle()) {showHelp('unknown_car');return;}
          if (stage === 'vehicle' && !cableRatingFor(selectedVehicle())) {showVehicleHelp(selectedVehicle());return;}
          if (stage === 'vehicle') setStage('finish');
          else if (stage === 'finish') showResult();
        },events);
        populate('make',unique(vehicles.map(v=>v.make)).map(x=>[x,x]),'select_make');
        setStage('mode',false);
        ui.hidden = false;
        fallback.hidden = true;
      }
      disconnectedCallback() {this.controller?.abort();}
    });
  }
})();

